import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import globalStore from 'src/stores/global-store';

import {
  IS_HEBREW,
  IS_LOGO_TEXT_SEPARATED,
  LOCALES,
  LOGO_IMAGE_PROPS,
  LogoSize,
  WITH_CUSTOM_LOGO_HEIGHT,
} from 'src/constants';
import { getLogoImageProps } from 'src/utils';
import type { LogoProps } from './logo.props';

import './logo.scss';

const Logo = observer(({ className, size }: LogoProps) => {
  const { cemeteryName } = globalStore;
  const ref = useRef<HTMLDivElement | null>(null);
  const isDefaultMediumImage =
    !IS_HEBREW && !IS_LOGO_TEXT_SEPARATED && !LOGO_IMAGE_PROPS && size === LogoSize.MEDIUM;

  const logoClassNames = classNames(
    'logo',
    `logo_${size}`,
    className,
    IS_HEBREW ? 'logo_hebrew' : 'logo_default',
    size !== LogoSize.SMALL && IS_LOGO_TEXT_SEPARATED && 'logo_text-separated'
  );

  const imageClassNames = classNames(
    'logo__image',
    isDefaultMediumImage && 'logo__image_default-medium'
  );

  useEffect(() => {
    if (WITH_CUSTOM_LOGO_HEIGHT && size === LogoSize.MEDIUM) {
      document.documentElement.style.setProperty('--logo-height', `${LOGO_IMAGE_PROPS!.height}px`);
    }
  }, [size]);

  useEffect(() => {
    if (
      !IS_LOGO_TEXT_SEPARATED ||
      !window.ResizeObserver ||
      !ref.current ||
      size !== LogoSize.MEDIUM
    ) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      if (entries[0]?.contentRect) {
        document.documentElement.style.setProperty(
          '--logo-height',
          `${entries[0].contentRect.height}px`
        );
      }
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [size]);

  const { APP_CEMETERY_LOGO_TITLE, APP_CEMETERY_LOGO_SUBTITLE } = window;

  return (
    <div className={logoClassNames} ref={ref}>
      <img
        alt={`${cemeteryName} ${LOCALES.LOGO}`}
        className={imageClassNames}
        {...getLogoImageProps(size)}
      />

      {IS_LOGO_TEXT_SEPARATED && size !== LogoSize.SMALL && (
        <p className="logo__text-wrapper">
          {APP_CEMETERY_LOGO_TITLE && (
            <span className="logo__title">{APP_CEMETERY_LOGO_TITLE}</span>
          )}
          {APP_CEMETERY_LOGO_SUBTITLE && (
            <span className="logo__subtitle">{APP_CEMETERY_LOGO_SUBTITLE}</span>
          )}
        </p>
      )}
    </div>
  );
});

export default Logo;
